import { textColorFields } from '../_shared/textColor.js';
import { universalFields, mediaRadiusField } from '../_shared/universal.js';
import { headingHighlightFields } from '../_shared/headingHighlight.js';
/**
 * image-slider — Shrine "Image/Video Slider" at CONFIG-BY-CONFIG parity, distilled to OUR token system.
 * A responsive CAROUSEL of slide blocks (image OR video) with a fully-configurable engine: classic / loop /
 * fade type, drag mode, autoplay, center mode, per-viewport (desktop + mobile) slides-per-page, scroll step,
 * spacing, preview side-padding, slide corner-radius, adaptive height, page-width, and arrows + dots colour
 * schemes & positions. The heading carries the shared word-HIGHLIGHT engine (2 engines × 8 styles) and the
 * section carries the shared 13-pattern BACKGROUND engine — both at full Shrine parity, exactly like
 * image-with-text.
 *
 * SHRINE PARITY MAP — every section setting and every child-block setting is implemented + wired, EXCEPT the
 * justified Shopify-platform-only knobs listed under DEFERRALS at the bottom of this file. Field ids are our
 * camelCase but map 1:1 to Shrine snake_case ids (noted inline where non-obvious).
 *
 * Two child block types (Shrine `image_slide` + `video_slide`), both repeatable/unlimited.
 */
const COLOR_SCHEME_OPTIONS = [
    { value: 'accent-1', label: 'Accent 1' },
    { value: 'accent-2', label: 'Accent 2' },
    { value: 'background-1', label: 'Background 1' },
    { value: 'background-2', label: 'Background 2' },
    { value: 'inverse', label: 'Inverse' },
];
export const imageSliderSchema = {
    type: 'image-slider',
    name: 'Image/Video slider',
    version: 3,
    templates: ['index', 'product', 'collection', 'page', 'thank-you'],
    fields: [
        // ── Visibility (Shrine `visibility`) ─────────────────────────────────────
        {
            id: 'visibility',
            type: 'select',
            label: 'Display on',
            group: 'General',
            default: 'always-display',
            options: [
                { value: 'always-display', label: 'All devices' },
                { value: 'mobile-hidden', label: 'Desktop only' },
                { value: 'desktop-hidden', label: 'Mobile only' },
            ],
        },
        // ── General: heading ─────────────────────────────────────────────────────
        {
            id: 'heading',
            type: 'text',
            label: 'Heading',
            group: 'General',
            default: 'Image/Video slider',
            defaultKey: 'section.image_slider',
            maxLength: 120,
            info: 'Wrap words in ** ** (highlight 1) or * * (highlight 2) to style them.',
        },
        // Heading highlight engine 1 + 2 (Shrine title_highlight_1 / _2 and all their sub-knobs).
        ...headingHighlightFields(1, 'Heading highlight 1'),
        ...headingHighlightFields(2, 'Heading highlight 2'),
        {
            id: 'headingSize',
            type: 'select',
            label: 'Heading size',
            group: 'General',
            default: 'h1',
            options: [
                { value: 'h3', label: 'Extra small' },
                { value: 'h2', label: 'Small' },
                { value: 'h1', label: 'Medium' },
                { value: 'h0', label: 'Large' },
                { value: 'custom', label: 'Custom' },
            ],
        },
        {
            id: 'customDesktopSize',
            type: 'range',
            label: 'Custom desktop size',
            group: 'General',
            min: 24,
            max: 60,
            step: 3,
            unit: 'px',
            default: 39,
            visibleWhen: { field: 'headingSize', equals: 'custom' },
        },
        {
            id: 'customMobileSize',
            type: 'range',
            label: 'Custom mobile size',
            group: 'General',
            min: 20,
            max: 50,
            step: 2,
            unit: 'px',
            default: 28,
            visibleWhen: { field: 'headingSize', equals: 'custom' },
        },
        // ── General: color scheme (Shrine color_scheme + custom_* overrides) ──────
        {
            id: 'colorSchemeChoice',
            type: 'select',
            label: 'Color scheme',
            group: 'General',
            default: 'background-1',
            options: [
                { value: 'accent-1', label: 'Accent 1' },
                { value: 'accent-2', label: 'Accent 2' },
                { value: 'background-1', label: 'Background 1' },
                { value: 'background-2', label: 'Background 2' },
                { value: 'inverse', label: 'Inverse' },
                { value: 'custom', label: 'Custom' },
            ],
        },
        {
            id: 'customColorsBackground',
            type: 'color',
            label: 'Custom background color',
            group: 'General',
            default: { ref: 'custom', value: '#FFFFFF' },
            visibleWhen: { field: 'colorSchemeChoice', equals: 'custom' },
        },
        {
            id: 'customGradientBackground',
            type: 'gradient',
            label: 'Custom gradient background',
            group: 'General',
            info: 'Replaces the background color when set.',
            visibleWhen: { field: 'colorSchemeChoice', equals: 'custom' },
        },
        {
            id: 'customImageBackground',
            type: 'image',
            label: 'Custom image background',
            group: 'General',
            visibleWhen: { field: 'colorSchemeChoice', equals: 'custom' },
        },
        {
            id: 'customMobileImageBackground',
            type: 'image',
            label: 'Custom mobile image background',
            group: 'General',
            visibleWhen: { field: 'colorSchemeChoice', equals: 'custom' },
        },
        {
            id: 'customColorsText',
            type: 'color',
            label: 'Custom text color',
            group: 'General',
            default: { ref: 'custom', value: '#121212' },
            visibleWhen: { field: 'colorSchemeChoice', equals: 'custom' },
        },
        // ── General: background pattern (shared 13-pattern engine, Shrine pattern_*) ─
        {
            id: 'patternBg',
            type: 'select',
            label: 'Background pattern',
            group: 'Background pattern',
            default: 'none',
            options: [
                { value: 'none', label: 'None' },
                { value: 'dotted', label: 'Dots' },
                { value: 'grid', label: 'Grid' },
                { value: 'curved-grid', label: 'Curved grid' },
                { value: 'diagonal-stripes', label: 'Stripes' },
                { value: 'zigzag', label: 'ZigZag' },
                { value: 'arrow-pattern', label: '3D Boxes' },
                { value: 'radial-rings', label: 'Rings' },
                { value: 'diamond-grid-1', label: 'Diamond grid 1' },
                { value: 'diamond-grid-2', label: 'Diamond grid 2' },
                { value: 'quatrefoils', label: 'Quatrefoils' },
                { value: 'ovals', label: 'Ovals' },
                { value: 'hexagons', label: 'Hexagons' },
                { value: 'curvy', label: 'Curvy' },
            ],
        },
        {
            id: 'patternColor',
            type: 'color',
            label: 'Pattern color',
            group: 'Background pattern',
            default: { ref: 'theme', token: 'accent' },
            visibleWhen: { field: 'patternBg', notEquals: 'none' },
        },
        {
            id: 'patternSize',
            type: 'range',
            label: 'Pattern size / spacing',
            group: 'Background pattern',
            min: 4,
            max: 80,
            step: 2,
            unit: 'px',
            default: 20,
            visibleWhen: { field: 'patternBg', notEquals: 'none' },
        },
        {
            id: 'patternThickness',
            type: 'range',
            label: 'Line / dot thickness',
            group: 'Background pattern',
            min: 1,
            max: 10,
            step: 1,
            unit: 'px',
            default: 2,
            visibleWhen: {
                field: 'patternBg',
                equals: ['dotted', 'grid', 'diagonal-stripes', 'radial-rings'],
            },
        },
        {
            id: 'patternAngle',
            type: 'range',
            label: 'Stripe angle',
            group: 'Background pattern',
            min: 0,
            max: 180,
            step: 15,
            unit: 'deg',
            default: 45,
            visibleWhen: { field: 'patternBg', equals: 'diagonal-stripes' },
        },
        {
            id: 'patternOpacityMode',
            type: 'select',
            label: 'Opacity type',
            group: 'Background pattern',
            default: 'low',
            options: [
                { value: 'low', label: 'Low' },
                { value: 'normal', label: 'Normal' },
            ],
            visibleWhen: { field: 'patternBg', notEquals: 'none' },
        },
        {
            id: 'patternOpacityLow',
            type: 'range',
            label: 'Opacity',
            group: 'Background pattern',
            min: 1,
            max: 20,
            step: 1,
            unit: '%',
            default: 7,
            visibleWhen: { field: 'patternOpacityMode', equals: 'low' },
        },
        {
            id: 'patternOpacityNormal',
            type: 'range',
            label: 'Opacity',
            group: 'Background pattern',
            min: 20,
            max: 100,
            step: 5,
            unit: '%',
            default: 80,
            visibleWhen: { field: 'patternOpacityMode', equals: 'normal' },
        },
        // ── Slider engine (Shrine type / drag / autoplay / center_mode / schemes) ─
        {
            id: 'sliderType',
            type: 'select',
            label: 'Type',
            group: 'Slider',
            default: 'slide',
            options: [
                { value: 'slide', label: 'Classic' },
                { value: 'loop', label: 'Infinite' },
                { value: 'fade', label: 'Fade' },
            ],
        },
        {
            id: 'drag',
            type: 'select',
            label: 'Drag',
            group: 'Slider',
            default: 'enabled',
            options: [
                { value: 'disabled', label: 'Disabled' },
                { value: 'enabled', label: 'Enabled' },
                { value: 'free', label: 'Free' },
            ],
        },
        {
            id: 'autoplay',
            type: 'checkbox',
            label: 'Enable autoplay',
            group: 'Slider',
            info: 'Carried as a hint for a future hydration island; manual arrows, dots and swipe always work.',
            default: false,
        },
        {
            id: 'autoplaySpeed',
            type: 'range',
            label: 'Autoplay speed',
            group: 'Slider',
            min: 1,
            max: 15,
            step: 0.5,
            unit: 'sec',
            default: 5,
            visibleWhen: { field: 'autoplay', equals: true },
        },
        {
            id: 'centerMode',
            type: 'checkbox',
            label: 'Enable center mode',
            group: 'Slider',
            default: false,
        },
        {
            id: 'arrowsScheme',
            type: 'select',
            label: 'Arrows color scheme',
            group: 'Slider',
            default: 'inverse',
            options: COLOR_SCHEME_OPTIONS,
        },
        {
            id: 'transparentArrows',
            type: 'checkbox',
            label: 'Transparent arrows',
            group: 'Slider',
            default: false,
        },
        {
            id: 'dotsScheme',
            type: 'select',
            label: 'Dots color',
            group: 'Slider',
            default: 'inverse',
            options: [
                { value: 'accent-1', label: 'Accent 1' },
                { value: 'accent-2', label: 'Accent 2' },
                { value: 'background-1', label: 'Background 1' },
                { value: 'background-2', label: 'Background 2' },
                { value: 'inverse', label: 'Text' },
            ],
        },
        // ── Desktop ──────────────────────────────────────────────────────────────
        // Shrine `desktop_page_width`: a slider-specific page width (4 options incl. edge-to-edge) applied to the
        // SLIDER wrapper ONLY — the heading keeps its own page width. This REPLACES the universal `width` control
        // (filtered out of the universalFields spread below), so the section has exactly ONE width control.
        {
            id: 'desktopPageWidth',
            type: 'select',
            label: 'Page width',
            group: 'Desktop',
            default: 'normal',
            options: [
                { value: 'page-width--narrow', label: 'Narrow' },
                { value: 'normal', label: 'Normal' },
                { value: 'desktop-full-page', label: 'Full width' },
                { value: 'desktop-full-page-no-padding', label: 'Full width without side spacing' },
            ],
        },
        {
            id: 'desktopBorderRadius',
            type: 'range',
            label: 'Slides corner radius',
            group: 'Desktop',
            min: 0,
            max: 60,
            step: 4,
            unit: 'px',
            default: 0,
        },
        {
            id: 'slidesDesktop',
            type: 'range',
            label: 'Slides per page',
            group: 'Desktop',
            min: 1,
            max: 10,
            step: 1,
            default: 3,
        },
        {
            id: 'perMoveDesktop',
            type: 'range',
            label: 'Slides to scroll on one move',
            group: 'Desktop',
            min: 1,
            max: 10,
            step: 1,
            default: 1,
        },
        {
            id: 'desktopSpacing',
            type: 'range',
            label: 'Spacing',
            group: 'Desktop',
            min: 0,
            max: 100,
            step: 4,
            unit: 'px',
            default: 28,
        },
        {
            id: 'desktopSidePadding',
            type: 'range',
            label: 'Preview of prev & next slide',
            group: 'Desktop',
            min: 0,
            max: 150,
            step: 5,
            unit: 'px',
            default: 0,
        },
        {
            id: 'desktopPaddingCalc',
            type: 'checkbox',
            label: 'Disable empty preview on first & last slide',
            group: 'Desktop',
            default: true,
        },
        {
            id: 'desktopAdaptiveHeight',
            type: 'checkbox',
            label: 'Adaptive height',
            group: 'Desktop',
            default: false,
        },
        {
            id: 'desktopDots',
            type: 'select',
            label: 'Dots position',
            group: 'Desktop',
            default: 'under',
            options: [
                { value: 'hidden', label: 'Hidden' },
                { value: 'under', label: 'Under the slider' },
                { value: 'over', label: 'Over the slider' },
            ],
        },
        {
            id: 'desktopArrows',
            type: 'select',
            label: 'Arrows position',
            group: 'Desktop',
            default: 'sides',
            options: [
                { value: 'hidden', label: 'Hidden' },
                { value: 'under', label: 'Under the slider' },
                { value: 'sides', label: 'On the sides' },
            ],
        },
        // ── Mobile ───────────────────────────────────────────────────────────────
        {
            id: 'mobileFullPage',
            type: 'checkbox',
            label: 'Full page width',
            group: 'Mobile',
            default: false,
        },
        {
            id: 'mobileBorderRadius',
            type: 'range',
            label: 'Slides corner radius',
            group: 'Mobile',
            min: 0,
            max: 60,
            step: 4,
            unit: 'px',
            default: 0,
        },
        {
            id: 'slidesMobile',
            type: 'range',
            label: 'Slides per page',
            group: 'Mobile',
            min: 1,
            max: 4,
            step: 1,
            default: 2,
        },
        {
            id: 'perMoveMobile',
            type: 'range',
            label: 'Slides to scroll on one move',
            group: 'Mobile',
            min: 1,
            max: 10,
            step: 1,
            default: 1,
        },
        {
            id: 'mobileSpacing',
            type: 'range',
            label: 'Spacing',
            group: 'Mobile',
            min: 0,
            max: 50,
            step: 2,
            unit: 'px',
            default: 12,
        },
        {
            id: 'mobileSidePadding',
            type: 'range',
            label: 'Preview of prev & next slide',
            group: 'Mobile',
            min: 0,
            max: 80,
            step: 4,
            unit: 'px',
            default: 0,
        },
        {
            id: 'mobilePaddingCalc',
            type: 'checkbox',
            label: 'Disable empty preview on first & last slide',
            group: 'Mobile',
            default: true,
        },
        {
            id: 'mobileAdaptiveHeight',
            type: 'checkbox',
            label: 'Adaptive height',
            group: 'Mobile',
            default: false,
        },
        {
            id: 'mobileDots',
            type: 'select',
            label: 'Dots position',
            group: 'Mobile',
            default: 'under',
            options: [
                { value: 'hidden', label: 'Hidden' },
                { value: 'under', label: 'Under the slider' },
                { value: 'over', label: 'Over the slider' },
            ],
        },
        {
            id: 'mobileArrows',
            type: 'select',
            label: 'Arrows position',
            group: 'Mobile',
            default: 'under',
            options: [
                { value: 'hidden', label: 'Hidden' },
                { value: 'under', label: 'Under the slider' },
                { value: 'sides', label: 'On the sides' },
            ],
        },
        // ── Section (universal: padding). Shrine padding_top/_bottom map to paddingTop/paddingBottom. The
        //    universal `width` control is filtered OUT here — the slider's width is governed by the Shrine
        //    `desktopPageWidth` select (Desktop group) so the section has exactly ONE width knob, not two. ──
        mediaRadiusField,
        // Text colors — the seller's own colour/gradient for this band's headings and body text.
        ...textColorFields(),
        ...universalFields.filter((f) => f.id !== 'width'),
    ],
    // Two child block types at full Shrine parity: an IMAGE slide and a VIDEO slide.
    blocks: [
        {
            type: 'image_slide',
            name: 'Image slide',
            fields: [
                { id: 'image', type: 'image', label: 'Image' },
                {
                    id: 'link',
                    type: 'link',
                    label: 'Link',
                    info: 'The whole slide becomes a clickable link.',
                },
                { id: 'description', type: 'richtext', label: 'Description' },
                {
                    id: 'descPlacement',
                    type: 'select',
                    label: 'Description placement',
                    default: 'overlay-bottom',
                    options: [
                        { value: 'overlay-bottom', label: 'Overlay – bottom (full width)' },
                        { value: 'overlay-bottom-left', label: 'Overlay – bottom left' },
                    ],
                },
                {
                    id: 'descAlignment',
                    type: 'select',
                    label: 'Description text alignment',
                    default: 'center',
                    options: [
                        { value: 'left', label: 'Left' },
                        { value: 'center', label: 'Center' },
                        { value: 'right', label: 'Right' },
                    ],
                },
                {
                    id: 'descColorScheme',
                    type: 'select',
                    label: 'Description color scheme',
                    default: 'background-2',
                    options: COLOR_SCHEME_OPTIONS,
                },
            ],
        },
        {
            type: 'video_slide',
            name: 'Video slide',
            fields: [
                // Spec 0020: a first-class uploaded video (MP4/WebM, upload-only) + a poster image. The player
                // controls below drive a real <video> on the storefront (via the Layout PE module).
                {
                    id: 'video',
                    type: 'video',
                    label: 'Video',
                    info: 'MP4 or WebM, uploaded (not a URL). Keep it short & compressed for speed.',
                },
                {
                    id: 'thumbnail',
                    type: 'image',
                    label: 'Video poster',
                    info: 'Shown before playback (and while it loads). Falls back to the first frame if empty.',
                },
                {
                    id: 'link',
                    type: 'link',
                    label: 'Link',
                    info: 'The whole slide becomes a clickable link.',
                },
                { id: 'loop', type: 'checkbox', label: 'Video looping', default: true },
                {
                    id: 'mutedAutoplay',
                    type: 'checkbox',
                    label: 'Muted autoplay',
                    info: 'Use instead of GIFs / animated WEBPs.',
                    default: true,
                },
                // Le disque lecture/pause de CETTE diapositive. ÉTEINT PAR DÉFAUT depuis le 20/09/2026
                // (fondatrice : « by default it shouldn't show ») — le carrousel le peignait sur toute vidéo qui
                // n'avait rien demandé. Le champ garde son id, son libellé et son texte d'aide : il existait
                // avant le champ partagé `showPlayButton` et porte, lui, une position/taille/couleur à lui, donc
                // le renommer aurait cassé les documents ET sept dictionnaires pour rien. La phrase « Auto-enabled
                // if autoplay is disabled » reste vraie : c'est `showsPlayButton` qui la tient.
                {
                    id: 'displayPlayBtn',
                    type: 'checkbox',
                    label: 'Enable play & pause on click',
                    info: 'Auto-enabled if autoplay is disabled.',
                    default: false,
                },
                // ── Buttons ────────────────────────────────────────────────────────
                {
                    id: 'playBtnPosition',
                    type: 'select',
                    label: 'Play button position',
                    group: 'Buttons',
                    default: 'center',
                    options: [
                        { value: 'center', label: 'Center' },
                        { value: 'top-left', label: 'Top left' },
                        { value: 'top-right', label: 'Top right' },
                        { value: 'bottom-right', label: 'Bottom right' },
                        { value: 'bottom-left', label: 'Bottom left' },
                    ],
                },
                {
                    id: 'btnColorScheme',
                    type: 'select',
                    label: 'Play button color scheme',
                    group: 'Buttons',
                    default: 'accent-1',
                    options: COLOR_SCHEME_OPTIONS,
                },
                {
                    id: 'playBtnStyle',
                    type: 'select',
                    label: 'Play button style',
                    group: 'Buttons',
                    default: 'solid',
                    options: [
                        { value: 'solid', label: 'Filled' },
                        { value: 'outline', label: 'Outlined' },
                    ],
                },
                {
                    id: 'playBtnSizeDesktop',
                    type: 'range',
                    label: 'Play button desktop size',
                    group: 'Buttons',
                    min: 30,
                    max: 80,
                    step: 5,
                    unit: 'px',
                    default: 50,
                },
                {
                    id: 'playBtnSizeMobile',
                    type: 'range',
                    label: 'Play button mobile size',
                    group: 'Buttons',
                    min: 25,
                    max: 75,
                    step: 5,
                    unit: 'px',
                    default: 50,
                },
                {
                    id: 'btnAnimation',
                    type: 'checkbox',
                    label: 'Enable button ripple animation',
                    group: 'Buttons',
                    default: false,
                },
                {
                    id: 'playBtnCornerRadius',
                    type: 'range',
                    label: 'Play button corner radius',
                    group: 'Buttons',
                    min: 0,
                    max: 100,
                    step: 5,
                    unit: '%',
                    default: 100,
                },
                {
                    id: 'displaySoundBtn',
                    type: 'checkbox',
                    label: 'Display mute/unmute button',
                    group: 'Buttons',
                    default: false,
                },
                {
                    id: 'soundBtnColorScheme',
                    type: 'select',
                    label: 'Sound button color scheme',
                    group: 'Buttons',
                    default: 'inverse',
                    options: COLOR_SCHEME_OPTIONS,
                    visibleWhen: { field: 'displaySoundBtn', equals: true },
                },
                {
                    id: 'displayFullscreenBtn',
                    type: 'checkbox',
                    label: 'Display fullscreen button',
                    group: 'Buttons',
                    default: false,
                },
                {
                    id: 'fullscreenBtnColorScheme',
                    type: 'select',
                    label: 'Fullscreen button color scheme',
                    group: 'Buttons',
                    default: 'inverse',
                    options: COLOR_SCHEME_OPTIONS,
                    visibleWhen: { field: 'displayFullscreenBtn', equals: true },
                },
                {
                    id: 'displayTimeline',
                    type: 'checkbox',
                    label: 'Display timeline',
                    group: 'Buttons',
                    default: false,
                },
                {
                    id: 'timelineColor',
                    type: 'select',
                    label: 'Timeline color',
                    group: 'Buttons',
                    default: 'accent-1',
                    options: [
                        { value: 'accent-1', label: 'Accent 1' },
                        { value: 'accent-2', label: 'Accent 2' },
                        { value: 'background-1', label: 'Background 1' },
                        { value: 'background-2', label: 'Background 2' },
                        { value: 'inverse', label: 'Text' },
                    ],
                    visibleWhen: { field: 'displayTimeline', equals: true },
                },
                // ── Description ────────────────────────────────────────────────────
                { id: 'description', type: 'richtext', label: 'Description' },
                {
                    id: 'descPlacement',
                    type: 'select',
                    label: 'Description placement',
                    default: 'overlay-bottom',
                    options: [
                        { value: 'overlay-bottom', label: 'Overlay – bottom (full width)' },
                        { value: 'overlay-bottom-left', label: 'Overlay – bottom left' },
                    ],
                },
                {
                    id: 'descAlignment',
                    type: 'select',
                    label: 'Description text alignment',
                    default: 'center',
                    options: [
                        { value: 'left', label: 'Left' },
                        { value: 'center', label: 'Center' },
                        { value: 'right', label: 'Right' },
                    ],
                },
                {
                    id: 'descColorScheme',
                    type: 'select',
                    label: 'Description color scheme',
                    default: 'background-2',
                    options: COLOR_SCHEME_OPTIONS,
                },
            ],
        },
    ],
    /**
     * Settings migration v1 → v2: the original section used a single `slide` block with `caption`/
     * `captionAlignment` and a `colorScheme` enum (light/inverse/accent) + `gap` + `slideRadius` ids. We map
     * the kept ids forward; renamed ids fall back to v2 defaults. (Blocks are migrated in the Component by
     * accepting the legacy `slide`/`caption` shape alongside the new ones.)
     */
    migrate(fromVersion, settings) {
        if (fromVersion >= 2)
            return settings;
        const next = { ...settings };
        if (typeof next.gap === 'number' && next.desktopSpacing == null)
            next.desktopSpacing = next.gap;
        if (typeof next.slideRadius === 'number' && next.desktopBorderRadius == null) {
            next.desktopBorderRadius = next.slideRadius;
            next.mobileBorderRadius = next.slideRadius;
        }
        return next;
    },
};
export const imageSliderPresets = [
    {
        name: 'Image/Video slider',
        settings: {
            visibility: 'always-display',
            heading: '@t:section.our_world',
            headingHighlight1: 'underline-solid',
            headingSize: 'h1',
            colorSchemeChoice: 'background-1',
            patternBg: 'none',
            sliderType: 'slide',
            drag: 'enabled',
            autoplay: false,
            autoplaySpeed: 5,
            centerMode: false,
            arrowsScheme: 'inverse',
            transparentArrows: false,
            dotsScheme: 'inverse',
            desktopPageWidth: 'normal',
            desktopBorderRadius: 0,
            slidesDesktop: 3,
            perMoveDesktop: 1,
            desktopSpacing: 28,
            desktopSidePadding: 0,
            desktopPaddingCalc: true,
            desktopAdaptiveHeight: false,
            desktopDots: 'under',
            desktopArrows: 'sides',
            mobileFullPage: false,
            mobileBorderRadius: 0,
            slidesMobile: 1,
            perMoveMobile: 1,
            mobileSpacing: 12,
            mobileSidePadding: 0,
            mobilePaddingCalc: true,
            mobileAdaptiveHeight: false,
            mobileDots: 'under',
            mobileArrows: 'under',
            colorScheme: 'light',
            paddingTop: 64,
            paddingBottom: 64,
            width: 'wide',
        },
        /*
         * Quatre diapos SANS média (direction fondatrice 2026-09-16) : l'aperçu du sélecteur comme la section
         * insérée montrent le placeholder scénique, jamais une photo maison. Historique : ce préréglage a
         * d'abord pointé `/demo/product-1.jpg` et `/demo/product-2.jpg`, qui n'ont jamais été livrés — deux
         * tuiles cassées dans l'aperçu. Les légendes restent en clair (le marqueur `@t:` n'est résolu que pour
         * les réglages chaîne de premier niveau et la forme rich-text `{html}`, jamais dans un ImageValue).
         */
        blocks: [
            {
                type: 'image_slide',
                settings: {
                    description: { format: 'html', html: "<p>Pensé dans l'atelier</p>" },
                    descAlignment: 'center',
                    descColorScheme: 'background-2',
                },
            },
            {
                type: 'image_slide',
                settings: {
                    description: { format: 'html', html: '<p>Des matières nobles</p>' },
                    descAlignment: 'center',
                    descColorScheme: 'background-2',
                },
            },
            {
                type: 'video_slide',
                settings: {
                    loop: true,
                    mutedAutoplay: true,
                    playBtnPosition: 'center',
                    btnColorScheme: 'accent-1',
                    playBtnStyle: 'solid',
                    description: { format: 'html', html: '<p>Porté au quotidien</p>' },
                    descAlignment: 'center',
                    descColorScheme: 'background-2',
                },
            },
            {
                type: 'image_slide',
                settings: {
                    description: { format: 'html', html: '<p>Des détails soignés</p>' },
                    descAlignment: 'center',
                    descColorScheme: 'background-2',
                },
            },
        ],
    },
];
/*
 * DEFERRALS (justified Shopify-platform-only, NOT implemented — every other Shrine setting is wired):
 *  - display_id ........... theme-editor section-group ID merge + copy-to-clipboard. No equivalent in a
 *                           custom builder (we key sections by nanoid id internally).
 *  - Slider PARAGRAPH note . a Shopify theme-editor dot-duplication caveat — informational text, not a setting.
 *  - padding HEADER / labels using t:sections.all.padding.* locale keys — Shopify translation keys; our
 *                           padding lives in the universal Section group with plain labels.
 *  - (RESOLVED in v3 / spec 0020) video source picker — the video_slide now has a first-class `video`
 *                           upload field (MP4/WebM, upload-only per the no-URL rule); all player-control
 *                           settings drive a real <video> on the storefront via the Layout PE module.
 *  - Shopify CDN srcset / img_url filters — our ImageValue carries width/height; native <img> is used.
 *  - reveal/scroll animations (animate-section / animate-item classes) — theme animation framework, not schema.
 */
/**
 * How this section presents itself in "Add section": label, group, icon.
 *
 * It lives beside the schema, NOT in `index.ts`, and that is the point. `index.ts` is the module that pulls
 * in `Component.tsx`, and the builder reads `meta` for all sixty-two sections to paint the picker while
 * rendering only the handful a page actually uses. Keeping `meta` here lets `registry.lazy.gen.ts` import it
 * without dragging every section's render code into the editor's entry chunk.
 */
export const imageSliderMeta = {
    label: 'Image/Video slider',
    group: 'Content',
    icon: 'image',
};
//# sourceMappingURL=schema.js.map